import { type AccountOpeningDTO, type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { type TrackingEvent } from '../../../store/metrics/metrics'
import { TrackEvent } from '../../../apps/professional-app/fa/common/tracking/models'
import { axisLabel, findAccountType } from './accountTypeCatalog'
import { holderClientStatus } from './contactFallback'
import { blockingIssues, operationSucceeded, storedStatusCode, type SubmissionOutcome } from './connectorResult'
import { namedHolders } from './parties'
import { isBlankValue, isRecord, schemaProperties } from './schemaValues'
import { type WizardAccount } from './types'

type TrackingDetails = Record<string, unknown>

const _VALIDATE_OPERATION = 'account-opening/validate'
const _SUBMIT_OPERATION = 'account-opening/submit'
const _SUBMITTED_WITHOUT_STATUS = 'Submitted'
const _DAY_MS = 24 * 60 * 60 * 1000
const _TRACK_ANNOTATION = 'x-zocks-track'

export const accountOpeningSource = (contactId: string | null, roomId: string | null = null, sessionId: string | null = null): string => {
  if (roomId && sessionId) {
    return 'past_meeting'
  }
  return contactId ? 'contact' : 'workspace'
}

const trackedValues = (account: WizardAccount): TrackingDetails =>
  Object.fromEntries(
    Object.entries(schemaProperties(account.schemaSnapshot)).flatMap(([key, node]) => {
      const property = isRecord(node) ? node[_TRACK_ANNOTATION] : undefined
      const value = account.values[key]
      return typeof property === 'string' && !isBlankValue(value) ? [[property, value]] : []
    }),
  )

export const accountTrackingLabel = (accountTypes: AccountType[], selectors: AccountOpeningTypeSelector[], typeId: string): string => {
  const type = findAccountType(accountTypes, typeId)
  const label = type
    ? selectors
        .map((selector) => axisLabel(type, selector.field))
        .filter(Boolean)
        .join(': ')
    : ''
  return label || typeId
}

export const accountsDetails = (accounts: WizardAccount[], accountTypes: AccountType[], selectors: AccountOpeningTypeSelector[]): TrackingDetails => ({
  new_account_count: accounts.length,
  new_accounts: accounts.map((account) => accountTrackingLabel(accountTypes, selectors, account.typeId)),
})

export const clickedEvent = (source: string): TrackingEvent => ({
  eventName: TrackEvent.ACCOUNT_OPENING_CLICKED,
  eventDetails: { source },
})

export const startedEvent = (
  source: string,
  accounts: WizardAccount[],
  accountTypes: AccountType[],
  selectors: AccountOpeningTypeSelector[],
): TrackingEvent => ({
  eventName: TrackEvent.ACCOUNT_OPENING_STARTED,
  eventDetails: { source, ...accountsDetails(accounts, accountTypes, selectors) },
})

export const validatedDetails = (outcome: SubmissionOutcome | null): TrackingDetails => {
  if (operationSucceeded(outcome)) {
    return { result: 'passed', error_count: 0 }
  }
  const issueCount = outcome ? blockingIssues(outcome.errors).length : 0
  return issueCount > 0 ? { result: 'errors', error_count: issueCount } : { result: 'failed', error_count: 0 }
}

export const submittedDetails = (
  source: string,
  accounts: WizardAccount[],
  accountTypes: AccountType[],
  selectors: AccountOpeningTypeSelector[],
  outcome: SubmissionOutcome | null,
): TrackingDetails => ({
  source,
  new_client: namedHolders(accounts).some((holder) => holderClientStatus(holder) === 'new'),
  ...accountsDetails(accounts, accountTypes, selectors),
  result: operationSucceeded(outcome) ? 'success' : 'failed',
  ...(accounts[0] ? trackedValues(accounts[0]) : {}),
})

export const operationTrackingEvent = (
  operation: string,
  source: string,
  accounts: WizardAccount[],
  accountTypes: AccountType[],
  selectors: AccountOpeningTypeSelector[],
  outcome: SubmissionOutcome | null,
): TrackingEvent | null => {
  if (operation === _VALIDATE_OPERATION) {
    return { eventName: TrackEvent.ACCOUNT_OPENING_VALIDATED, eventDetails: validatedDetails(outcome) }
  }
  if (operation === _SUBMIT_OPERATION) {
    return { eventName: TrackEvent.ACCOUNT_OPENING_SUBMITTED, eventDetails: submittedDetails(source, accounts, accountTypes, selectors, outcome) }
  }
  return null
}

export const daysSince = (timestamp: string | null | undefined, now: number = Date.now()): number | undefined => {
  const at = timestamp ? Date.parse(timestamp) : NaN
  return Number.isNaN(at) ? undefined : Math.max(0, Math.floor((now - at) / _DAY_MS))
}

const dayCount = (key: string, timestamp: string | null | undefined): TrackingDetails => {
  const days = daysSince(timestamp)
  return days === undefined ? {} : { [key]: days }
}

export const openingTrackingStatus = (opening: AccountOpeningDTO): string =>
  opening.status === 'draft' ? 'draft' : (storedStatusCode(opening.metadata) ?? _SUBMITTED_WITHOUT_STATUS)

export const resumedDetails = (opening: AccountOpeningDTO): TrackingDetails => dayCount('days_since_created', opening.createdAt)

export const envelopeCheckedDetails = (opening: AccountOpeningDTO): TrackingDetails => ({
  status: openingTrackingStatus(opening),
  ...dayCount('days_since_submitted', opening.submittedAt),
})

export const deletedDetails = (opening: AccountOpeningDTO): TrackingDetails => ({
  status: openingTrackingStatus(opening),
  ...dayCount('days_since_created', opening.createdAt),
})
